/**
 * Local-First Knockout Tournament Engine.
 *
 * Single-elimination bracket. Players whose names go into the setup form
 * are seeded in the order entered. If the count is not a power of 2, the
 * top seeds receive BYE slots in round 1.
 *
 * Data shape:
 *   tournament = {
 *     id, name, createdAt, completedAt,
 *     presetId: 'CLASSIC_21' | 'BWF_3x15',
 *     players: ['Alice', 'Bob', ...],
 *     rounds: [ [ match, match, ... ], ... ]
 *   }
 *   match = {
 *     id, roundIdx, slotIdx,
 *     sideA: 'Alice' | null,
 *     sideB: 'Bob'   | null,
 *     winner: 'A' | 'B' | null,
 *     scoreLine: '21-15, 21-19' | null,
 *     completedAt: string | null
 *   }
 */

const STORAGE_KEY = 'loveall:tournaments';

const uuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const nextPow2 = (n) => {
  let p = 1;
  while (p < n) p *= 2;
  return p;
};

/** Build empty rounds for n players (with BYEs to reach power of 2). */
function buildEmptyBracket(players) {
  const n = players.length;
  const size = Math.max(2, nextPow2(n));
  // Padded list: real players first, then BYE markers (null).
  const seeded = [...players, ...Array(size - n).fill(null)];

  // Round 0: pair (0 vs size-1), (1 vs size-2), …  standard bracket.
  // For simplicity (and because byes go to top seeds) we'll pair sequentially:
  // (0,1), (2,3), …  with top-half byes auto-advancing.
  const rounds = [];
  let firstRound = [];
  for (let i = 0; i < size; i += 2) {
    firstRound.push({
      id: uuid(),
      roundIdx: 0,
      slotIdx: i / 2,
      sideA: seeded[i],
      sideB: seeded[i + 1],
      winner: null,
      scoreLine: null,
      completedAt: null,
    });
  }
  rounds.push(firstRound);

  // Subsequent rounds (empty slots, filled by winners).
  let prev = firstRound;
  while (prev.length > 1) {
    const next = [];
    for (let i = 0; i < prev.length; i += 2) {
      next.push({
        id: uuid(),
        roundIdx: rounds.length,
        slotIdx: i / 2,
        sideA: null,
        sideB: null,
        winner: null,
        scoreLine: null,
        completedAt: null,
      });
    }
    rounds.push(next);
    prev = next;
  }

  // Auto-advance BYEs in round 0
  rounds[0].forEach(m => {
    if (m.sideA && !m.sideB) { m.winner = 'A'; m.scoreLine = 'BYE'; m.completedAt = new Date().toISOString(); }
    else if (!m.sideA && m.sideB) { m.winner = 'B'; m.scoreLine = 'BYE'; m.completedAt = new Date().toISOString(); }
  });
  // Propagate BYE winners into round 1
  if (rounds.length > 1) {
    rounds[0].forEach((m, idx) => {
      if (m.winner === 'A' || m.winner === 'B') {
        propagateWinner(rounds, 0, idx, m.winner === 'A' ? m.sideA : m.sideB);
      }
    });
  }
  return rounds;
}

function propagateWinner(rounds, roundIdx, slotIdx, winnerName) {
  if (roundIdx + 1 >= rounds.length) return;
  const nextSlot = Math.floor(slotIdx / 2);
  const isLeft = slotIdx % 2 === 0;
  const next = rounds[roundIdx + 1][nextSlot];
  if (isLeft) next.sideA = winnerName;
  else next.sideB = winnerName;
}

/** Create + persist a new tournament. */
export function createTournament({ name, players, presetId = 'CLASSIC_21' }) {
  if (!Array.isArray(players) || players.length < 2) {
    throw new Error('At least 2 players required');
  }
  const cleaned = players.map(p => (p || '').trim()).filter(Boolean);
  if (cleaned.length < 2) throw new Error('At least 2 valid player names required');
  const t = {
    id: uuid(),
    name: (name || 'Untitled Tournament').trim(),
    presetId,
    createdAt: new Date().toISOString(),
    completedAt: null,
    players: cleaned,
    rounds: buildEmptyBracket(cleaned),
  };
  const all = listTournaments();
  all.unshift(t);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  return t;
}

export function listTournaments() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
  catch { return []; }
}

export function getTournament(id) {
  return listTournaments().find(t => t.id === id) || null;
}

export function deleteTournament(id) {
  const all = listTournaments().filter(t => t.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}

/** Record the result of a match → propagate winner & possibly complete tournament. */
export function recordMatchResult(tournamentId, matchId, { winner, scoreLine }) {
  const all = listTournaments();
  const t = all.find(x => x.id === tournamentId);
  if (!t) throw new Error('Tournament not found');
  let target = null;
  for (const round of t.rounds) {
    const m = round.find(x => x.id === matchId);
    if (m) { target = m; break; }
  }
  if (!target) throw new Error('Match not found');
  target.winner = winner;
  target.scoreLine = scoreLine;
  target.completedAt = new Date().toISOString();

  const winnerName = winner === 'A' ? target.sideA : target.sideB;
  propagateWinner(t.rounds, target.roundIdx, target.slotIdx, winnerName);

  // Mark tournament complete if final has a winner
  const final = t.rounds[t.rounds.length - 1][0];
  if (final.winner) {
    t.completedAt = new Date().toISOString();
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  return { tournament: t, isComplete: !!t.completedAt, championName: t.completedAt ? winnerName : null };
}

export function tournamentStatus(t) {
  if (t.completedAt) return 'completed';
  const anyResult = t.rounds.some(r => r.some(m => m.winner));
  return anyResult ? 'in_progress' : 'scheduled';
}

export function tournamentChampion(t) {
  if (!t.completedAt) return null;
  const final = t.rounds[t.rounds.length - 1][0];
  return final.winner === 'A' ? final.sideA : final.sideB;
}

export const ROUND_NAMES = (totalRounds, roundIdx) => {
  // Last round = Final; second-last = Semifinal; third-last = Quarterfinal; else "Round N"
  const fromEnd = totalRounds - 1 - roundIdx;
  if (fromEnd === 0) return 'Final';
  if (fromEnd === 1) return 'Semifinals';
  if (fromEnd === 2) return 'Quarterfinals';
  return `Round ${roundIdx + 1}`;
};
